<template>
  <div>
    <div class="flex space-x-3 p-2 px-4" :style="{
      backgroundColor: tourSettings?.header_background_color || '#ffffff',
      color: tourSettings?.header_text_color || '#000000'
    }">
      <!-- Logo section -->
      <div class="flex h-16 items-center">
        <Logo />
      </div>

      <!-- Center section with indicators -->
      <div class="flex-grow flex justify-center items-center">
        <div v-if="isGameModuleActive && statusIndicators.length > 0" class="flex items-center space-x-4">
          <div v-for="indicator in statusIndicators" :key="indicator.source.reference" 
               class="flex items-center space-x-2 px-3 py-2 rounded-lg">
            <!-- Icon if available -->
            <img v-if="indicator.icon?.preview_url" 
                 :src="indicator.icon.preview_url" 
                 :alt="indicatorName(indicator)"
                 :title="indicatorName(indicator)"
                 class="w-6 h-6 object-contain" />
            <!-- Fallback to name if no icon -->
            <span v-else class="text-sm font-medium">{{ indicatorName(indicator) }}</span>
            <!-- Value -->
            <span class="text-2xl">{{ getIndicatorValue(indicator) }}</span>
          </div>
        </div>
      </div>

      <!-- Variables Button (only show if game module is active) -->
      <div v-if="isGameModuleActive && tourSettings?.game?.debug_mode" class="flex h-16 items-center">
        <div class="flex w-12 h-12 items-center">
          <div class="rounded-full p-3 hover:cursor-pointer flex flex-col text-sm items-center"
               :style="{ background: tourSettings?.header_background_contrast_color || '#f3f4f6' }"
               @click="showVariables">
               <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
  <path stroke-linecap="round" stroke-linejoin="round" d="M21 16.5V7.5a2.25 2.25 0 0 0-1.2-1.98l-6.75-3.75a2.25 2.25 0 0 0-2.1 0l-6.75 3.75A2.25 2.25 0 0 0 3 7.5v9a2.25 2.25 0 0 0 1.2 1.98l6.75 3.75a2.25 2.25 0 0 0 2.1 0l6.75-3.75A2.25 2.25 0 0 0 21 16.5z" />
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.27 6.96l8.73 4.86 8.73-4.86M12 21.75v-9.99" />
</svg>
          </div>
        </div>
      </div>

      <!-- Inventory Button (only show if game module is active) -->
      <div v-if="isGameModuleActive" class="flex h-16 items-center">
        <div class="flex w-12 h-12 items-center">
          <!-- Custom Icon if available -->
          <img v-if="inventoryIcon" 
               :src="inventoryIcon" 
               :alt="$t('inventory', 'Inventory')"
               class="w-full h-full object-cover cursor-pointer" 
               @click="showInventory" />
          <!-- Default Icon -->
          <div v-else
               class="rounded-full p-3 hover:cursor-pointer flex flex-col text-sm items-center"
               :style="{ background: tourSettings?.header_background_contrast_color || '#f3f4f6' }"
               @click="showInventory">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                 stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M21 12a2.25 2.25 0 0 0-2.25-2.25H15a3 3 0 1 1-6 0H5.25A2.25 2.25 0 0 0 3 12m18 0v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 9m18 0V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v3"/>
            </svg>
          </div>
        </div>
      </div>

      <div 
        v-if="tourSettings?.id && (tourSettings?.introduction_page_id || tourSettings?.languages?.length > 1)"
        class="flex"
      >
        <div class="flex h-16 items-center">
          <div
            class="rounded-full p-3 hover:cursor-pointer flex flex-col text-sm items-center"
            :style="{ background: tourSettings?.header_background_contrast_color || '#f3f4f6' }"
            @click="handleMenuClick"
          >
            <svg class="h-6 w-6 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { h, markRaw } from 'vue'
import Logo from './Logo.vue'
import Inventory from './Inventory.vue'
import Variables from './Variables.vue'
import moduleManager from '../modules/index.js'
import { showMultiLingualText } from '../utils/languageUtils.js'
import gameModule from "../modules/GameModule";

export default {
  name: 'VueHeader',
  components: {
    Logo
  },
  props: {
    isMenuOpen: {
      type: Boolean,
      default: false
    },
    toggleMenu: {
      type: Function,
      required: true
    }
  },
  computed: {
    ...mapGetters('settings', ['tourSettings']),
    
    // Check if game module is active
    isGameModuleActive() {
      return moduleManager.isModuleActive('game')
    },
    
    // Get inventory icon from tour settings
    inventoryIcon() {
      return this.tourSettings?.game?.inventory_icon?.preview_url || this.tourSettings?.game?.inventory_icon
    },
    
    // Get total inventory items count
    totalInventoryItems() {
      const gameState = moduleManager.getModuleData('game')
      if (!gameState?.inventory) return 0
      
      return Object.values(gameState.inventory).reduce((total, item) => {
        return total + (item.quantity || 0)
      }, 0)
    },
    
    statusIndicators() {
      return this.tourSettings?.game?.indicators || []
    },

  },
  methods: {
    handleMenuClick() {
      this.toggleMenu()
    },
    
    showInventory() {
      console.log("showInventory from header")
      
      // Create Inventory component for overlay
      const InventoryComponent = markRaw({
        name: 'InventoryWrapper',
        components: { Inventory },
        setup() {
          return () => h(Inventory)
        }
      })
      
      // Get inventory title from tour settings or fallback to translation
      const inventoryTitle = showMultiLingualText(
        this.tourSettings?.game?.inventory_title,
        this.$t('game.inventory'),
        false,
        this.$i18n.locale?.substring(0, 2)
      )
      
      // Show Inventory page in overlay using Vuex store
      this.$store.dispatch('overlay/openOverlay', {
        component: InventoryComponent,
        title: inventoryTitle,
        props: {}
      })
    },
    
    showVariables() {
      console.log("showVariables from header")
      
      // Create Variables component for overlay
      const VariablesComponent = markRaw({
        name: 'VariablesWrapper',
        components: { Variables },
        setup() {
          return () => h(Variables)
        }
      })
      
      // Show Variables page in overlay using Vuex store
      this.$store.dispatch('overlay/openOverlay', {
        component: VariablesComponent,
        title: this.$t('variables'),
        props: {}
      })
    },
    
    // Get multilingual name for indicator
    indicatorName(indicator) {
      return showMultiLingualText(
        indicator.name,
        indicator.source.reference, // fallback to reference
        false,
        this.$i18n.locale?.substring(0, 2)
      )
    },
    
    // Get value for indicator based on source type
    getIndicatorValue(indicator) {
      const gameState = moduleManager.getModuleData('game')
      if (!gameState) return 0
      
      if (indicator.source.type === 'variable') {
        // Get value from variables
        return gameModule.getVariableValue(indicator.source.reference) || 0
      } else if (indicator.source.type === 'object') {
        // Get quantity from inventory
        const inventoryItem = gameModule.getInventoryItem(indicator.source.reference)
        return inventoryItem?.quantity || 0
      }
      
      return 0
    }
  }
}
</script> 